import { useEffect, useState } from 'react';
import {
  useTranslate,
  useNotification,
  useInvalidate,
  useCustomMutation,
} from '@refinedev/core';
import { Modal, Form, InputNumber, Input } from 'antd';
import { extractBackendMessage } from '@/utils/apiError';
import type { LedgerEntryIssueIn } from '@/api/generated/schemas';

type LedgerEntryAddValues = LedgerEntryIssueIn;

interface LedgerEntryAddModalProps {
  entitlementId: number;
  open: boolean;
  onClose: () => void;
}

export const LedgerEntryAddModal = ({
  entitlementId,
  open,
  onClose,
}: LedgerEntryAddModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation();
  const [form] = Form.useForm<LedgerEntryAddValues>();
  const [submitting, setSubmitting] = useState(false);
  const deltaSeconds = Form.useWatch('delta_seconds', form);
  const reason = Form.useWatch('reason', form);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSubmitting(false);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    let values: LedgerEntryAddValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      await mutateAsync({
        url: `monetization/entitlements/${entitlementId}/ledger-entries`,
        method: 'post',
        values,
        successNotification: false,
        errorNotification: false,
      });

      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: t('monetization.entitlements.ledger.add.success'),
      });
      invalidate({
        resource: 'monetization.entitlements',
        invalidates: ['detail'],
        id: entitlementId,
      });
      onClose();
    } catch (error) {
      const description =
        extractBackendMessage(error) ??
        t('monetization.entitlements.ledger.add.error_fallback');
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const hasDelta = typeof deltaSeconds === 'number' && deltaSeconds !== 0;
  const submitLabel = hasDelta
    ? t('monetization.entitlements.ledger.add.submit_signed', {
        delta: deltaSeconds > 0 ? `+${deltaSeconds}` : String(deltaSeconds),
      })
    : t('monetization.entitlements.ledger.add.submit');

  const submitDisabled =
    !hasDelta || !reason || reason.trim() === '' || submitting;

  return (
    <Modal
      open={open}
      title={t('monetization.entitlements.ledger.add.title')}
      okText={submitLabel}
      okButtonProps={{ disabled: submitDisabled, loading: submitting }}
      cancelText={t('common.cancel')}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
      >
        <Form.Item
          name="delta_seconds"
          label={t('monetization.entitlements.ledger.add.fields.delta_seconds')}
          rules={[
            {
              required: true,
              message: t(
                'monetization.entitlements.ledger.add.errors.delta_required'
              ),
            },
          ]}
          extra={t('monetization.entitlements.ledger.add.hints.delta_seconds')}
        >
          <InputNumber style={{ width: '100%' }} step={60} placeholder="1800" />
        </Form.Item>
        <Form.Item
          name="reason"
          label={t('monetization.entitlements.ledger.add.fields.reason')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t(
                'monetization.entitlements.ledger.add.errors.reason_required'
              ),
            },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder={t('monetization.entitlements.ledger.add.hints.reason')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
